/**
 * Template editor — Unlayer drag & drop, with an HTML fallback.
 *
 * Unlayer is loaded from its CDN as a plain <script>. Two things matter:
 *
 *  1. embed.js is UMD, so with RequireJS present it registers as an anonymous
 *     AMD module and never reaches `window.unlayer`. We hide `define.amd`
 *     while it loads and restore it after — the same trick this dashboard
 *     already uses for html2canvas.
 *  2. If the CDN is unreachable (offline, blocked), the editor falls back to a
 *     raw HTML textarea rather than showing nothing. A template stays editable
 *     and sendable either way, because `htmlContent` is what we actually send —
 *     `designJson` only exists so Unlayer can reopen its own document.
 *
 * Images: Unlayer's own upload needs a (paid) project with storage, so the
 * `image` callback is registered instead — a file dropped on an Image block, or
 * chosen through its "Upload Image" button, is sent to our image library and the
 * block gets the returned URL. "Images" opens that same library to reuse an
 * earlier upload: in the designer a click adds it as an Image block at the
 * bottom of the design (same saveDesign / loadDesign route as the row library;
 * "Copy URL" is there for swapping the picture of an existing block), in HTML
 * mode it inserts an <img> at the cursor.
 *
 * The row library ("Rows") saves a row of the current design — a header, a
 * footer, a product block — and drops it into any other template. Unlayer's own
 * saved-blocks feature is a paid one; this does the same through the public
 * saveDesign / loadDesign calls: a saved row is appended to the design's rows.
 */
define('mailingTemplateEditorView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'mailingImageLibrary',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  ImageLibrary,
  MailingApi
) {
  'use strict';

  var UNLAYER_SCRIPT = 'https://editor.unlayer.com/embed.js';
  var MERGE_TAGS = [
    'first_name', 'last_name', 'email', 'country_code', 'language',
    'webshop', 'webshop_locale', 'webshop_locale_phone',
    'offer_email_seq_picture1', 'offer_email_seq_url1', 'offer_email_seq_poi1',
    'offer_email_seq_price1', 'offer_email_seq_quantity1',
    'personalised_image',
    'end_user_sec_id', 'unsubscribe_url', 'proofsheet_url'
  ];

  /** What a row contains, for telling rows apart in the library panel: content types, first text, background. */
  function describeRow(row) {
    var contents = _.flatten(_.map((row || {}).columns || [], function (column) {
      return column.contents || [];
    }));
    var firstText = _.find(contents, function (content) { return content.type === 'text'; });
    var text = firstText && firstText.values ? String(firstText.values.text || '') : '';
    return {
      types: _.uniq(_.map(contents, function (content) { return content.type; })),
      snippet: $.trim(text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ')).substring(0, 60),
      background: (row.values || {}).backgroundColor || '#ffffff'
    };
  }

  /**
   * A one-column row holding one Image block, in Unlayer's design format, with
   * ids taken from (and written back to) the design's counters so they cannot
   * collide with existing blocks. Wider than the 600px body → Unlayer scales it.
   */
  function imageRow(design, image) {
    var counters = design.counters = design.counters || {};
    function next(key) {
      counters[key] = (counters[key] || 0) + 1;
      return counters[key];
    }
    var rowId = next('u_row');
    var columnId = next('u_column');
    var imageId = next('u_content_image');
    return {
      cells: [1],
      columns: [{
        contents: [{
          type: 'image',
          values: {
            containerPadding: '10px',
            src: { url: image.url, width: image.width || 600, height: image.height || 400 },
            textAlign: 'center',
            altText: '',
            action: { name: 'web', values: { href: '', target: '_blank' } },
            hideDesktop: false,
            selectable: true, draggable: true, duplicatable: true, deletable: true,
            _meta: { htmlID: 'u_content_image_' + imageId, htmlClassNames: 'u_content_image' }
          }
        }],
        values: { _meta: { htmlID: 'u_column_' + columnId, htmlClassNames: 'u_column' } }
      }],
      values: {
        columns: false, backgroundColor: '', columnsBackgroundColor: '', padding: '0px',
        selectable: true, draggable: true, duplicatable: true, deletable: true,
        _meta: { htmlID: 'u_row_' + rowId, htmlClassNames: 'u_row' }
      }
    };
  }

  /** Loads embed.js once per page; resolves with window.unlayer or rejects. */
  function loadUnlayer() {
    var defer = $.Deferred();
    if (window.unlayer) {
      return defer.resolve(window.unlayer).promise();
    }
    var amd = window.define ? window.define.amd : null;
    if (window.define) {
      window.define.amd = null;
    }
    var script = document.createElement('script');
    script.src = UNLAYER_SCRIPT;
    script.onload = function () {
      if (window.define) {
        window.define.amd = amd;
      }
      if (window.unlayer) {
        defer.resolve(window.unlayer);
      } else {
        defer.reject();
      }
    };
    script.onerror = function () {
      if (window.define) {
        window.define.amd = amd;
      }
      defer.reject();
    };
    document.head.appendChild(script);
    return defer.promise();
  }

  var TemplateEditorView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-template-editor',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-editor-head">' +
      '  <label class="inline">Name<input type="text" class="f-name"></label>' +
      '  <label class="inline grow">Subject<input type="text" class="f-subject"></label>' +
      '  <label class="inline narrow">Language<input type="text" class="f-language"></label>' +
      '</div>' +
      '<div class="mlg-merge-tags"></div>' +
      '<div class="mlg-editor-host"><div id="mailing-unlayer" class="mlg-unlayer"></div></div>' +
      '<div class="mlg-modal" style="display:none"></div>' +
      '<div class="mlg-rows-panel" style="display:none"></div>'
    ),

    ui: {
      rowsPanel: '.mlg-rows-panel',
      name: '.f-name',
      subject: '.f-subject',
      language: '.f-language',
      host: '.mlg-editor-host',
      tags: '.mlg-merge-tags',
      modal: '.mlg-modal'
    },

    events: {
      'click .mlg-save-template': 'onSave',
      'click .mlg-back': 'onBack',
      'click .mlg-send-test': 'onSendTest',
      'click .mlg-modal-cancel': 'closeModal',
      'click .mlg-modal-send': 'onSendTestConfirm',
      'click .mlg-open-images': 'onOpenImages',
      'click .t-tag': 'onCopyTag',
      'click .mlg-open-rows': 'onOpenRows',
      'click .mlg-rows-close': 'closeRows',
      'click .mlg-row-save': 'onSaveRow',
      'click .mlg-row-insert': 'onInsertRow',
      'click .mlg-row-delete': 'onDeleteRow'
    },

    initialize: function (options) {
      this.secId = options.secId;
      this.mode = 'unlayer';
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('templates',
          '<button type="button" class="mlg-btn mlg-open-images" title="Upload images, or reuse an uploaded one">Images</button>' +
          '<button type="button" class="mlg-btn mlg-open-rows" title="Save a row for reuse, or insert a saved one">Rows</button>' +
          '<button type="button" class="mlg-btn mlg-send-test">Send test</button>' +
          '<button type="button" class="mlg-btn primary mlg-save-template">Save</button>' +
          '<a class="mlg-btn mlg-back" href="#mailing/templates">Back to list</a>')
      };
    },

    onShow: function () {
      var self = this;
      this.ui.tags.html('<span class="t-label">Merge fields — click to copy:</span>' +
        _.map(MERGE_TAGS, function (tag) {
          return '<code class="t-tag">{{' + tag + '}}</code>';
        }).join(''));

      MailingApi.template(this.secId).done(function (template) {
        self.model = template;
        self.ui.name.val(template.name || '');
        self.ui.subject.val(template.subject || '');
        self.ui.language.val(template.language || 'EN');
        self.mountEditor(template);
      }).fail(function (xhr) {
        self.ui.host.html('<div class="mlg-error">' + _.escape(MailingApi.errorMessage(xhr)) + '</div>');
      });
    },

    mountEditor: function (template) {
      var self = this;
      loadUnlayer().done(function (unlayer) {
        try {
          unlayer.init({
            id: 'mailing-unlayer',
            displayMode: 'email',
            mergeTags: _.reduce(MERGE_TAGS, function (tags, tag) {
              tags[tag] = { name: Shell.prettyCase(tag), value: '{{' + tag + '}}' };
              return tags;
            }, {})
          });
          unlayer.registerCallback('image', function (file, done) {
            self.uploadFromEditor(file, done);
          });
          var design = self.parseDesign(template.designJson);
          if (design) {
            unlayer.loadDesign(design);
          }
          self.unlayer = unlayer;
        } catch (error) {
          self.mountFallback(template, 'The drag & drop editor could not start.');
        }
      }).fail(function () {
        self.mountFallback(template,
          'The drag & drop editor could not be loaded (editor.unlayer.com unreachable).');
      });
    },

    /** Raw-HTML editing, used whenever Unlayer is unavailable. */
    mountFallback: function (template, reason) {
      this.mode = 'html';
      this.ui.host.html(
        '<div class="mlg-warning">' + _.escape(reason) +
          ' Editing the HTML source instead — it is what gets sent.</div>' +
        '<textarea class="mlg-html-source" spellcheck="false"></textarea>');
      this.ui.host.find('.mlg-html-source').val(template.htmlContent || '');
    },

    parseDesign: function (raw) {
      if (!raw) { return null; }
      try {
        var design = JSON.parse(raw);
        return design && design.body ? design : null;
      } catch (error) {
        return null;
      }
    },

    /**
     * Collects the current document. Unlayer's export is asynchronous, so this
     * hands back a promise in both modes and the callers stay identical.
     */
    currentContent: function () {
      var defer = $.Deferred();
      if (this.mode === 'html') {
        defer.resolve({
          htmlContent: this.ui.host.find('.mlg-html-source').val(),
          designJson: this.model ? this.model.designJson : ''
        });
        return defer.promise();
      }
      if (!this.unlayer) {
        defer.reject();
        return defer.promise();
      }
      this.unlayer.exportHtml(function (data) {
        defer.resolve({
          htmlContent: data.html,
          designJson: JSON.stringify(data.design)
        });
      });
      return defer.promise();
    },

    onSave: function () {
      var self = this;
      this.currentContent().done(function (content) {
        MailingApi.updateTemplate(self.secId, {
          name: self.ui.name.val(),
          subject: self.ui.subject.val(),
          language: self.ui.language.val(),
          htmlContent: content.htmlContent,
          designJson: content.designJson
        }).done(function () {
          Shell.banner(self.$el, 'Template saved', 'ok');
        }).fail(function (xhr) {
          Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
        });
      }).fail(function () {
        Shell.banner(self.$el, 'The editor is not ready yet', 'error');
      });
    },

    onSendTest: function () {
      this.ui.modal.html(
        '<div class="mlg-modal-card narrow">' +
        '<h3>Send a test</h3>' +
        '<label>Recipient<input type="email" class="f-test-email" placeholder="you@example.com"></label>' +
        '<div class="mlg-hint">Merge fields are filled with sample data. The test bypasses the outbox ' +
          'and its hourly limit.</div>' +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Cancel</button>' +
        '  <button type="button" class="mlg-btn primary mlg-modal-send">Send</button>' +
        '</div>' +
        '</div>').show();
    },

    onSendTestConfirm: function () {
      var self = this;
      var email = this.ui.modal.find('.f-test-email').val();
      if (!email || email.indexOf('@') === -1) {
        Shell.banner(this.$el, 'A valid recipient is required', 'error');
        return;
      }
      this.currentContent().done(function (content) {
        MailingApi.sendTestEmail({
          email: email,
          subject: self.ui.subject.val(),
          htmlContent: content.htmlContent
        }).done(function () {
          self.closeModal();
          Shell.banner(self.$el, 'Test sent to ' + email, 'ok');
        }).fail(function (xhr) {
          Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
        });
      });
    },

    closeModal: function () {
      this.ui.modal.hide().empty();
    },

    //----------Images-------------------------------------------------------//

    /**
     * Unlayer's upload hook. `done` must always be called — with a url on
     * success, without one on failure — or the block's spinner never stops.
     */
    uploadFromEditor: function (upload, done) {
      var self = this;
      var file = upload && upload.attachments ? upload.attachments[0] : null;
      var problem = file ? ImageLibrary.refusal(file) : 'No file received';
      if (problem) {
        Shell.banner(this.$el, problem, 'error');
        done({ progress: 100 });
        return;
      }
      done({ progress: 20 });
      MailingApi.uploadImage(file).done(function (image) {
        done({ progress: 100, url: image.url });
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
        done({ progress: 100 });
      });
    },

    onOpenImages: function () {
      var self = this;
      var htmlMode = this.mode === 'html';
      ImageLibrary.open(this.$el, {
        pickLabel: htmlMode ? 'Insert at the cursor' : 'Add to the template',
        onPick: function (image) {
          if (htmlMode) {
            self.insertIntoSource('<img src="' + _.escape(image.url) + '" alt="" width="' +
              Math.min(image.width || 600, 600) + '" style="max-width:100%;height:auto;border:0;">');
            Shell.banner(self.$el, '"' + image.name + '" inserted', 'ok');
            return;
          }
          self.insertImageBlock(image);
        },
        // For replacing the picture of a block that is already laid out.
        secondary: {
          label: 'Copy URL',
          onPick: function (image, $status) {
            Shell.copyText(image.url).done(function (copied) {
              if (copied) {
                $status.removeClass('error').text('URL copied — paste it in an Image block\'s "Image URL" field');
              } else {
                window.prompt('Copy this URL into the Image block\'s "Image URL" field:', image.url);
              }
            });
          }
        }
      });
    },

    insertImageBlock: function (image) {
      var self = this;
      if (!this.unlayer) {
        Shell.banner(this.$el, 'The editor is not ready yet', 'error');
        return;
      }
      this.unlayer.saveDesign(function (design) {
        design = design || {};
        design.body = design.body || {};
        design.body.rows = (design.body.rows || []).concat([imageRow(design, image)]);
        self.unlayer.loadDesign(design);
        Shell.banner(self.$el, '"' + image.name + '" added at the bottom — drag it where it belongs', 'ok');
      });
    },

    /** Replaces the selection (or inserts at the caret) in the HTML-source textarea. */
    insertIntoSource: function (text) {
      var area = this.ui.host.find('.mlg-html-source')[0];
      if (!area) { return; }
      var start = area.selectionStart === undefined ? area.value.length : area.selectionStart;
      var end = area.selectionEnd === undefined ? start : area.selectionEnd;
      area.value = area.value.substring(0, start) + text + area.value.substring(end);
      area.selectionStart = area.selectionEnd = start + text.length;
      area.focus();
    },

    /** The merge-field strip says "click to copy"; this is the click. */
    onCopyTag: function (e) {
      var self = this;
      var tag = $(e.currentTarget).text();
      Shell.copyText(tag).done(function (copied) {
        if (copied) {
          Shell.banner(self.$el, tag + ' copied', 'ok');
        }
      });
    },

    //----------Row library--------------------------------------------------//

    onOpenRows: function () {
      var self = this;
      if (this.mode !== 'unlayer' || !this.unlayer) {
        Shell.banner(this.$el, 'The row library needs the drag & drop editor', 'error');
        return;
      }
      // saveDesign rather than the last loaded design: the rows offered for
      // saving must be the ones on screen, unsaved edits included.
      this.unlayer.saveDesign(function (design) {
        self.designRows = ((design || {}).body || {}).rows || [];
        MailingApi.savedRows().done(function (savedRows) {
          self.renderRows(savedRows || []);
        }).fail(function (xhr) {
          Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
        });
      });
    },

    renderRows: function (savedRows) {
      this.savedRows = savedRows;
      var current = _.map(this.designRows, function (row, index) {
        var info = describeRow(row);
        return '<div class="mlg-row-card" data-index="' + index + '">' +
          '<div class="r-head"><span class="r-swatch" style="background:' + _.escape(info.background) + '"></span>' +
          'Row ' + (index + 1) +
          _.map(info.types, function (type) { return '<span class="r-type">' + _.escape(type) + '</span>'; }).join('') +
          '</div>' +
          (info.snippet ? '<div class="r-snippet">' + _.escape(info.snippet) + '</div>' : '') +
          '<div class="r-save"><input type="text" class="f-row-label" placeholder="Name this row…">' +
          '<button type="button" class="mlg-btn small primary mlg-row-save">Save</button></div>' +
          '</div>';
      }).join('');
      var saved = _.map(savedRows, function (row) {
        return '<div class="mlg-row-saved" data-secid="' + row.secId + '">' +
          '<button type="button" class="mlg-row-insert" title="Insert at the bottom of this template">' +
            _.escape(row.label || 'Untitled') + '</button>' +
          '<button type="button" class="mlg-row-delete" title="Delete from the library">&times;</button>' +
          '</div>';
      }).join('');

      this.ui.rowsPanel.html(
        '<div class="mlg-rows-card">' +
        '<div class="r-title">Row library<button type="button" class="mlg-rows-close">&times;</button></div>' +
        '<h4>Save a row from this template</h4>' +
        (current || '<div class="mlg-hint">This design has no row yet.</div>') +
        '<h4>Saved rows — click to insert</h4>' +
        (saved || '<div class="mlg-hint">Nothing saved yet. Save a row above.</div>') +
        '</div>').show();
    },

    closeRows: function () {
      this.ui.rowsPanel.hide().empty();
    },

    onSaveRow: function (e) {
      var self = this;
      var $card = $(e.currentTarget).closest('.mlg-row-card');
      var index = Number($card.data('index'));
      var label = $.trim($card.find('.f-row-label').val()) || 'Row ' + (index + 1);
      MailingApi.createSavedRow({
        label: label,
        tags: '[]',
        body: JSON.stringify(this.designRows[index])
      }).done(function (row) {
        Shell.banner(self.$el, '"' + label + '" saved to the library', 'ok');
        self.renderRows(self.savedRows.concat([row]));
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onInsertRow: function (e) {
      var self = this;
      var secId = $(e.currentTarget).closest('.mlg-row-saved').data('secid');
      var saved = _.findWhere(this.savedRows, { secId: secId });
      var row;
      try {
        row = JSON.parse(saved.body);
      } catch (error) {
        Shell.banner(this.$el, 'This saved row is damaged and cannot be inserted', 'error');
        return;
      }
      this.unlayer.saveDesign(function (design) {
        design.body = design.body || {};
        design.body.rows = (design.body.rows || []).concat([row]);
        self.unlayer.loadDesign(design);
        self.closeRows();
        Shell.banner(self.$el, '"' + (saved.label || 'Row') + '" inserted at the bottom — drag it where it belongs', 'ok');
      });
    },

    onDeleteRow: function (e) {
      var self = this;
      var secId = $(e.currentTarget).closest('.mlg-row-saved').data('secid');
      var saved = _.findWhere(this.savedRows, { secId: secId }) || {};
      if (!window.confirm('Delete "' + (saved.label || 'this row') + '" from the library? Templates using it keep their copy.')) {
        return;
      }
      MailingApi.deleteSavedRow(secId).done(function () {
        self.renderRows(_.reject(self.savedRows, function (row) { return row.secId === secId; }));
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onBack: function (e) {
      e.preventDefault();
      App.navigate('mailing/templates', { trigger: true });
    }
  });

  module.exports = TemplateEditorView;
});

